import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  XMarkIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

type ChatMessage = {
  id?: string | number;
  message?: string;
  sender_type?: string;
  senderType?: string;
  senderName?: string;
  timestamp?: string;
  created_at?: string;
};

type SocketLike = {
  connected?: boolean;
  on: (event: string, cb: (...args: any[]) => void) => void;
  emit: (event: string, payload?: any) => void;
  disconnect: () => void;
};

declare global {
  interface Window {
    io?: (url: string, opts?: Record<string, unknown>) => SocketLike;
  }
}

const SESSION_KEY = 'airmax_support_chat_session';

function digitsOnly(value: string) {
  return value.replace(/\D/g, '');
}

function formatPhoneInput(value: string) {
  const d = digitsOnly(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 11)}`;
}

function isValidUsPhone(value: string) {
  const d = digitsOnly(value);
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
}

function killSalesWidget() {
  // Never show the website sales chat bubble on the support chat page
  const styleId = 'hide-airmax-sales-chat';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      [id*="airmax-chat"], [class*="airmax-chat"],
      #airmax-chat-widget, #airmax-chat-window, #airmax-greeting-bubble, #airmax-chat-button {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  const w = window as Window & {
    AirmaxChat?: {
      isOpen?: boolean;
      greetingBubbleShown?: boolean;
      hideGreetingBubble?: () => void;
      hide?: () => void;
      toggle?: () => void;
    };
  };
  if (w.AirmaxChat) {
    try {
      if (w.AirmaxChat.isOpen) {
        w.AirmaxChat.isOpen = false;
        const win = document.getElementById('airmax-chat-window');
        if (win) win.style.display = 'none';
      }
      w.AirmaxChat.greetingBubbleShown = false;
      w.AirmaxChat.hideGreetingBubble?.();
      w.AirmaxChat.hide?.();
    } catch {
      /* ignore */
    }
  }

  ['airmax-chat-widget', 'airmax-chat-window', 'airmax-greeting-bubble'].forEach((id) => {
    document.getElementById(id)?.remove();
  });
}

function loadSocketIo(): Promise<NonNullable<typeof window.io>> {
  return new Promise((resolve, reject) => {
    if (window.io) {
      resolve(window.io);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-airmax-socketio]');
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.io) resolve(window.io);
        else reject(new Error('Socket.IO failed to load'));
      });
      existing.addEventListener('error', () => reject(new Error('Socket.IO failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.socket.io/4.5.4/socket.io.min.js';
    script.async = true;
    script.dataset.airmaxSocketio = '1';
    script.onload = () => {
      if (window.io) resolve(window.io);
      else reject(new Error('Socket.IO failed to load'));
    };
    script.onerror = () => reject(new Error('Socket.IO failed to load'));
    document.head.appendChild(script);
  });
}

function formatMsgTime(msg: ChatMessage) {
  const raw = msg.timestamp || msg.created_at;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function renderMessageHtml(text: string) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

const Chat: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [clientName, setClientName] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [visitorId, setVisitorId] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(true);
  const socketRef = useRef<SocketLike | null>(null);
  const messagesPaneRef = useRef<HTMLDivElement | null>(null);

  // Always suppress sales widget on this page
  useEffect(() => {
    killSalesWidget();
    const timer = window.setInterval(killSalesWidget, 1500);
    return () => {
      window.clearInterval(timer);
      document.getElementById('hide-airmax-sales-chat')?.remove();
    };
  }, []);

  // Keep new messages visible inside the popup only
  useEffect(() => {
    const pane = messagesPaneRef.current;
    if (!pane || !widgetOpen) return;
    pane.scrollTop = pane.scrollHeight;
  }, [messages, widgetOpen, connecting]);

  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  const connectSupportChat = async (convId: string, visId: string) => {
    setConnecting(true);
    try {
      // Load history
      const histRes = await fetch(`https://airmaxcloud.com/api/chat/conversations/${convId}/messages`);
      if (histRes.ok) {
        const hist = (await histRes.json()) as ChatMessage[];
        if (Array.isArray(hist)) setMessages(hist);
      }

      const io = await loadSocketIo();
      socketRef.current?.disconnect();
      const socket = io('https://airmaxcloud.com', {
        transports: ['websocket', 'polling'],
        query: { visitor_id: visId, visitorId: visId },
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join-conversation', { conversationId: convId });
      });

      socket.on('new-message', (message: ChatMessage) => {
        setMessages((prev) => {
          if (message.id != null && prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      });
    } finally {
      setConnecting(false);
    }
  };

  const startSupportChat = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidUsPhone(phone)) {
      setError('Enter a valid 10-digit U.S. phone number to start support chat.');
      return;
    }

    setLoading(true);
    killSalesWidget();
    try {
      const res = await fetch('/api/support-chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ phone, name }),
      });
      const data = (await res.json().catch(() => null)) as
        | {
            ok?: boolean;
            error?: string;
            conversationId?: string;
            visitorId?: string;
            clientName?: string;
            phone?: string;
          }
        | null;

      if (!res.ok || !data?.ok || !data.conversationId || !data.visitorId) {
        throw new Error(data?.error || 'Unable to start support chat.');
      }

      setConversationId(data.conversationId);
      setVisitorId(data.visitorId);
      setClientName(data.clientName || name || 'Customer');
      if (data.phone) setPhone(data.phone);
      setWidgetOpen(true);
      setUnlocked(true);
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          conversationId: data.conversationId,
          visitorId: data.visitorId,
          clientName: data.clientName,
          phone: data.phone || phone,
          channelType: 'mobile_app',
        })
      );
      await connectSupportChat(data.conversationId, data.visitorId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start support chat.');
    } finally {
      setLoading(false);
    }
  };

  // Resume session after refresh — only keep real phone-app support threads (mobile_*)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        conversationId?: string;
        visitorId?: string;
        clientName?: string;
        phone?: string;
        channelType?: string;
      };
      if (!saved.conversationId || !saved.visitorId) return;
      // Drop old web_support_* / sales-style sessions so users re-enter the real support queue
      if (!String(saved.visitorId).startsWith('mobile_')) {
        sessionStorage.removeItem(SESSION_KEY);
        return;
      }
      setConversationId(saved.conversationId);
      setVisitorId(saved.visitorId);
      setClientName(saved.clientName || '');
      if (saved.phone) setPhone(saved.phone);
      setWidgetOpen(true);
      setUnlocked(true);
      void connectSupportChat(saved.conversationId, saved.visitorId);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !conversationId || !socketRef.current) return;

    // Same payload shape as airmaxcloud.com/app/support
    socketRef.current.emit('send-message', {
      conversationId,
      senderType: 'visitor',
      senderId: conversationId,
      message: text,
      imageUrl: null,
    });

    // Optimistic local echo
    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        message: text,
        sender_type: 'visitor',
        timestamp: new Date().toISOString(),
      },
    ]);
    setDraft('');
  };

  const resetSession = () => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    sessionStorage.removeItem(SESSION_KEY);
    setUnlocked(false);
    setWidgetOpen(true);
    setMessages([]);
    setConversationId('');
    setVisitorId('');
    setDraft('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="relative py-14 md:py-16 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/15 mb-6">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Support Chat</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Same live support chat used in the Airmax phone app — not sales chat.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!unlocked ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Enter your account phone</h2>
              <p className="text-gray-600 mb-6">
                Use the phone number on your Airmax account to open technical support chat.
              </p>

              <form onSubmit={startSupportChat} className="space-y-5 max-w-lg">
                {error ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                ) : null}

                <div>
                  <label htmlFor="support-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Account phone number *
                  </label>
                  <div className="relative">
                    <PhoneIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      id="support-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                      placeholder="(928) 555-1234"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="support-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    id="support-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  {loading ? 'Connecting to support…' : 'Start Support Chat'}
                </button>
              </form>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl bg-blue-50 p-4 text-blue-900 flex gap-3">
                  <ShieldCheckIcon className="h-5 w-5 shrink-0" />
                  Opens the support queue used by the phone app — agents and support AI, not sales.
                </div>
                <div className="rounded-xl bg-gray-50 p-4 text-gray-700">
                  Prefer calling?{' '}
                  <a href="tel:928-565-6707" className="text-airmax-blue font-semibold hover:underline">
                    928-565-6707
                  </a>
                  <div className="mt-2">
                    <Link to="/support" className="text-airmax-blue font-semibold hover:underline">
                      Support Center →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-8 max-w-lg"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">You’re connected</h2>
                  <p className="text-gray-600 mt-1">
                    Support chat is open in the corner — same layout as most websites.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    {clientName || 'Customer'}
                    {phone ? ` · ${phone}` : ''}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={() => setWidgetOpen(true)} className="btn-primary inline-flex items-center gap-2">
                      <ChatBubbleLeftRightIcon className="h-5 w-5" />
                      Open chat
                    </button>
                    <button
                      type="button"
                      onClick={resetSession}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <ArrowPathIcon className="h-5 w-5" />
                      Change number
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Familiar bottom-right chat popup (Intercom / Zendesk style) */}
      {unlocked ? (
        <div className="fixed z-[80] bottom-5 right-5 flex flex-col items-end gap-3 pointer-events-none">
          {widgetOpen ? (
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="pointer-events-auto w-[min(100vw-1.5rem,380px)] h-[min(70vh,520px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
              role="dialog"
              aria-label="Airmax support chat"
            >
              <div className="px-4 py-3 bg-airmax-blue text-white flex items-center gap-3 shrink-0">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-tight truncate">Airmax Support</p>
                  <p className="text-xs text-blue-100 truncate">
                    {connecting ? 'Connecting…' : `Chatting as ${clientName || 'Customer'}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setWidgetOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-white/15"
                  aria-label="Minimize chat"
                  title="Minimize"
                >
                  <MinusIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={resetSession}
                  className="p-1.5 rounded-lg hover:bg-white/15"
                  aria-label="End chat"
                  title="End chat"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div ref={messagesPaneRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 bg-[#f7f8fb]">
                {connecting && messages.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm py-10">Connecting to support…</p>
                ) : null}
                {!connecting && messages.length === 0 ? (
                  <div className="text-center py-8 px-2">
                    <p className="font-semibold text-gray-900 text-sm">Hi {clientName || 'there'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Ask about your connection, billing, or account. An Airmax agent is here to help.
                    </p>
                  </div>
                ) : null}
                {messages.map((msg, idx) => {
                  const isVisitor = msg.sender_type === 'visitor' || msg.senderType === 'visitor';
                  return (
                    <div key={String(msg.id ?? idx)} className={`flex mb-2.5 ${isVisitor ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${
                          isVisitor
                            ? 'bg-airmax-blue text-white rounded-br-md'
                            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                        }`}
                      >
                        {!isVisitor ? (
                          <p className="text-[11px] font-semibold mb-0.5 text-gray-500">
                            {msg.senderName || 'Support'}
                          </p>
                        ) : null}
                        <div
                          className="text-[13px] leading-snug whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: renderMessageHtml(String(msg.message || '')) }}
                        />
                        {formatMsgTime(msg) ? (
                          <p className={`text-[10px] mt-1 ${isVisitor ? 'text-blue-100' : 'text-gray-400'}`}>
                            {formatMsgTime(msg)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendMessage} className="p-3 border-t border-gray-200 bg-white flex gap-2 shrink-0">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-airmax-blue focus:border-transparent outline-none"
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || !conversationId}
                  className="btn-primary disabled:opacity-50 px-4 text-sm"
                >
                  Send
                </button>
              </form>
            </motion.div>
          ) : null}

          <button
            type="button"
            onClick={() => setWidgetOpen((open) => !open)}
            className="pointer-events-auto w-14 h-14 rounded-full bg-airmax-blue text-white shadow-xl hover:bg-blue-700 flex items-center justify-center"
            aria-label={widgetOpen ? 'Minimize support chat' : 'Open support chat'}
          >
            {widgetOpen ? <XMarkIcon className="h-7 w-7" /> : <ChatBubbleLeftRightIcon className="h-7 w-7" />}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default Chat;
